<template>
  <div class="filterListNode">
    <div
      v-for="group in partitioned"
      :key="group.parent ? group.parent.id : 'root'"
    >
      <div v-if="group.parent && (isExpanded || expandedStates[group.id])">
        <div style="display: flex; align-items: center;">
          <div class="isParent columns">
            <label class="b-checkbox checkbox">
              <input
                ref="checkboxRef"
                class="column"
                type="checkbox"
                :checked="checkedMap.get(group)"
                :indeterminate="indeMap.get(group)"
                @change="onBranchCheck(group, ($event.target as HTMLInputElement).checked)"
              >
              <span class="fake-checkbox" /> <!-- Ajoute un span vide pour la fausse coche stylée en CSS -->
            </label>
            <p class="labelCheckBox">
              {{ localizedNode(group.parent) }}
            </p>
            <b-icon
              :icon="(expandedStates[group.id])?'chevron-down':'chevron-up'"
              style="float: right; margin-bottom: 1em;"
              @click="switchExpanded(group.id)"
            />
          </div>
        </div>
        <div style="margin-left: 1rem;">
          <FilterListNodeCheck
            v-if="expandedStates[group.id]"
            :is-expanded="expandedStates[group.id]"
            :leaves="group.leaves"
            :selected-leaves="selectedLeaves"
            :parent-rank="parentRank + 1"
            @leaf-checked="emitLeafChecked"
          />
        </div>
      </div>
      <template v-else-if="!group.parent && isExpanded">
        <div
          v-for="leaf in group.leaves"
          :key="leaf.id"
          class="withoutParent columns"
        >
          <label class="b-checkbox checkbox">
            <input
              type="checkbox"
              :checked="isChecked(leaf)"
              @change="onLeafCheck(leaf, $event)"
            >
            <span class="fake-checkbox" /> <!-- Ajoute un span vide pour la fausse coche stylée en CSS -->
          </label>
          <p class="labelCheckBox">
            <!--            <TitleCardWithLimitLength :limit="20" :local-name="leaf.__display_fr || leaf.__display_default"/>-->
            {{ localizedNode(leaf) }}
          </p>
        </div>
      </template>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { computed } from "vue";
import FilterListNodeCheck from "./FilterListNodeCheck.vue";
import { BIcon } from "buefy";
import useObject from "@/composable/components/object";
import { RefsLinked } from "@/types/generated/UsedGeneratedTypes";
import { FilterListNode, FilterListNodeParams } from "@/model/data/FilterListTree";
// import truncate from "@/composable/components/title";
// import TitleCardWithLimitLength from "@/components/common/TitleCardWithLimitLength.vue";

const props = defineProps({
  locale: { type: String, default: "" },
  defaultLocale: { type: String, default: "" },
  isExpanded: { type: Boolean, default: false },
  leaves: { type: Object, required: true },
  selectedLeaves: { type: Array, required: true },
  parentRank: { type: Number, required: true },
});
const { reactiveObject: expandedStates } = useObject(props.leaves.reduce((acc: any, leaf: RefsLinked) => {
  acc[leaf.id] = false;
  return acc;
}, {}));

const checkedMap = computed(() => {
  const set = new Set(props.selectedLeaves);
  const map = new Map();
  for (const group of partitioned.value) {
    map.set(group, group.leaves.every((l: RefsLinked) => set.has(l.naturalKey.sql)));
  }
  return map;
});

const localizedNode = function (node: FilterListNodeParams) {
  if (props.locale != props.defaultLocale && node["__display_" + props.locale] && node["__display_" + props.locale] != "") {
    return node["__display_" + props.locale];
  }
  return node["__display_" + props.defaultLocale] || node.__display_default;
};

const indeMap = computed(() => {
  const set = new Set(props.selectedLeaves);
  const map = new Map();
  for (const group of partitioned.value) {
    const all = group.leaves.every((l: RefsLinked) => set.has(l.naturalKey.sql));
    const some = group.leaves.some((l: RefsLinked) => set.has(l.naturalKey.sql));
    map.set(group, !all && some);
  }
  return map;
});

const isChecked = function (leaf: RefsLinked) {
  return props.selectedLeaves.includes(leaf.naturalKey.sql);
};

const emit = defineEmits(["leaf-checked"]);
const switchExpanded = function (id: string) {
  let states = expandedStates;
  states[id] = !states[id];
  expandedStates.value = states;
};

function partitionByParent(leaves: FilterListNode[], rank: any) {
  const groups = new Map();
  let expanding = {};
  for (const leaf of leaves) {
    const parent = leaf.getParent(rank);
    const key = parent ? parent.id : null;
    if (!groups.has(key)) groups.set(key, { parent, leaves: [], id: leaf.id });
    groups.get(key).leaves.push(leaf);
  }

  for (const group of groups.values()) {
    group.leaves.sort((a: any, b: any) =>
      (a.naturalKey.sql || "").localeCompare(b.naturalKey.sql || ""),
    );
  }
  expandedStates.value = expanding;
  return Array.from(groups.values());
}

const partitioned = computed(() => partitionByParent(props.leaves as FilterListNode[], props.parentRank));

function onBranchCheck(group: any, checked: boolean) {
  emit("leaf-checked", { selection: group.leaves.map((leaf: RefsLinked) => leaf.naturalKey.sql), checked });
}

function emitLeafChecked(...args: any) {
  emit("leaf-checked", ...args);
}

function onLeafCheck(leaf: RefsLinked, event: Event) {
  emit("leaf-checked", {
    selection: [leaf.naturalKey.sql],
    checked: (event.target as HTMLInputElement).checked,
  });
}

</script>
<style lang="scss" scoped>
.filterListNode {
  .isParent {
    margin-bottom: 1em;
    border-bottom: thick double #009d9d;
    width: 100%;
    .labelCheckBox {
      width: 80% !important;
    }
  }
  .withoutParent {
    max-width: 100%;
    min-width: 60%;
    overflow: hidden;
    .checkbox {
      width: 12%;
    }
    .labelCheckBox {
      width: 88% !important;
    }
  }
  .b-checkbox.checkbox {
    height: auto;
    overflow: hidden;
    input[type="checkbox"] {
      opacity: 0;
      position: absolute;
      left: 0;
      top: 0;
      width: 1.25em;
      height: 1.25em;
      margin-right: .5em;
    }
    /* Quand indéterminé, on insère l'unicode de l'icon Moins avec :after */
    input[type="checkbox"]:indeterminate + .fake-checkbox::after {
      content: "\002212";
      font-weight: 900;
      color: white;
      display: flex;
      align-items: center;
      justify-content: center;
      width: 100%;
      height: 100%;
      position: absolute;
      left: 0;
      top: 0;

    }
    /* Quand checké, on insère l'unicode de l'icon Check avec :after */
    input[type="checkbox"]:checked + .fake-checkbox::after {
      content: "\002713";
      font-weight: bold; /* fa-solid */
      display: flex;
      align-items: center;
      justify-content: center;
      font-size: 1em;
      color: white;
      background: $primary;
      width: 100%;
      height: 100%;
      position: absolute;
      left: 0;
      top: 0;
    }
    .fake-checkbox {
      position: relative;
      display: inline-block;
      width: 1.25em;
      height: 1.25em;
      border: 2px solid $primary;
      border-radius: 4px;
      transition: border-color 0.2s, background 0.2s;
      vertical-align: middle;
      margin-right: 0.25em;
      min-width: 1.25em;
    }
  }
}
</style>
